// libs
import React from 'react';

import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { StackProps } from '@lyearn/ui/src/Stack/types';
import { ProviderType } from '@/types/schema';

import { ActionItemProvider as ContextProvider } from '../../context';
// components
import { ActionItemFragment } from '../../graphql/fragments/generated/ActionItem';
import type useActionItemActions from '../../hooks/useActionItemActions';
import ActionDueDatePicker from '../ActionDueDatePicker';
import ActionIdentifier from '../ActionIdentifier';
import ActionItemLinkedTo from '../ActionItemLinkedTo';
import ActionList from '../ActionList';
import ActionListLink from '../ActionListLink';
import ActionName from '../ActionName';
import ActionOptions from '../ActionOptions';
import ActionPriority from '../ActionPriority';
import ActionProject from '../ActionProject';
import ActionProjectLink from '../ActionProjectLink';
import ActionStage from '../ActionStage';
import ActionStatus from '../ActionStatus';
import AssignMenu from '../AssignMenu';
import CreatedOn from '../CreatedOn';
import Provider from '../Provider';
import StaticAssignMenu from '../StaticAssignMenu';
import TaskListActions from '../TaskListActions';
import TaskProviderStatus from '../TaskProviderStatus';

type Props = {
  children: React.ReactNode;
  actionItem: ActionItemFragment;
  actions?: ReturnType<typeof useActionItemActions>['actions'];
  className?: string;
  provider?: ProviderType;
  isReadOnly?: boolean;
  disableInteractiveTooltip?: boolean;
} & StackProps;

const ActionItemProvider: React.FC<Props> = (props) => {
  const {
    actionItem,
    children,
    actions,
    className,
    provider,
    isReadOnly,
    disableInteractiveTooltip = false,
    ...restStackProps
  } = props;

  return (
    <ContextProvider
      actionItem={actionItem}
      actions={actions}
      disableInteractiveTooltip={disableInteractiveTooltip}
      isReadOnly={isReadOnly}
      provider={provider}>
      <Stack alignItems="center" className={cn('w-full', className)} {...restStackProps}>
        {children}
      </Stack>
    </ContextProvider>
  );
};

export {
  AssignMenu as AssignMenu,
  CreatedOn,
  ActionDueDatePicker as DatePicker,
  ActionIdentifier as Identifier,
  ActionItemLinkedTo as LinkedTo,
  ActionList as List,
  TaskListActions as ListActions,
  ActionListLink as ListLink,
  ActionName as Name,
  ActionOptions as Options,
  ActionPriority as Priority,
  ActionProject as Project,
  ActionProjectLink as ProjectLink,
  ActionItemProvider as Provider,
  TaskProviderStatus as ProviderStatus,
  ActionStage as Stage,
  StaticAssignMenu,
  ActionStatus as Status,
  Provider as TaskProvider,
};
